import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PhoneOutgoing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, useLoad, errMsg, SLOTS, fmtTime } from "@/lib/api";
import { PageHead, card, Field, Sel, inputCls, PatientSelect, ResultBadge, Empty } from "@/components/admin/kit";
import { ScheduleTestCall } from "@/components/admin/ScheduleTestCall";

export default function TestCall() {
  const api = useApi();
  const [settings] = useLoad("/settings");
  const [q, setQ] = useState({ patient_id: "", slot: "morning", to: "" });
  const [logs, reload] = useLoad("/call-logs?limit=200");
  const tests = (logs || []).filter((l) => l.test_call);
  const live = tests.some((l) => l.result === "pending");

  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(reload, 3000);
    return () => clearInterval(t);
  }, [live, reload]);

  const call = async () => {
    try {
      const r = (await api.post("/calls/test", q)).data;
      r.result === "failed" ? toast.error(r.error) : toast.success("Call placed — answer your phone");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const prov = settings?.call_provider;
  return (
    <div data-testid="test-call-page">
      <PageHead title="Real test call" sub="Optional. Calls a verified test number and plays the stored 8 kHz voice. The demo works without this." />
      <div className={`${card} mb-4 text-sm`} data-testid="test-call-provider">
        Active provider: <b className="capitalize">{prov || "…"}</b>
        {prov === "simulator" && <span className="text-muted-foreground"> — choose Twilio (or Exotel later) in Settings once its keys are added.</span>}
        <p className="mt-1 text-xs text-muted-foreground">Twilio trial: the number must be verified in Twilio, India must be on in Voice Geo Permissions, and Twilio plays a short trial message first.</p>
      </div>
      <div className={`${card} grid gap-3 md:grid-cols-4`}>
        <Field label="Patient (needs consent)"><PatientSelect value={q.patient_id} onChange={(v) => setQ({ ...q, patient_id: v })} testid="test-patient-select" /></Field>
        <Field label="Time slot"><Sel value={q.slot} onChange={(v) => setQ({ ...q, slot: v })} options={SLOTS} testid="test-slot-select" /></Field>
        <Field label="Verified test number" hint="e.g. +91 98xxxxxxxx"><input className={inputCls} value={q.to} onChange={(e) => setQ({ ...q, to: e.target.value })} data-testid="test-to-input" /></Field>
        <div className="flex items-end"><Button className="w-full" disabled={!q.patient_id || !q.to || prov === "simulator"} onClick={call} data-testid="test-call-btn"><PhoneOutgoing size={16} /> Call this number now</Button></div>
      </div>
      <ScheduleTestCall prov={prov} />
      <div className={`${card} mt-4`}>
        <p className="mb-3 font-bold">Test calls</p>
        {!tests.length ? <Empty>No real test calls yet.</Empty> : tests.map((l) => (
          <div key={l.id} className="border-b border-[var(--lav-2)] py-3 text-sm last:border-0" data-testid={`test-log-${l.id}`}>
            <div className="flex flex-wrap items-center gap-2"><b>{l.patient_name}</b><span className="text-muted-foreground">{l.kind}{l.slot ? ` · ${l.slot}` : ""} · {l.provider} · {l.to_masked}{l.scheduled_id ? " · scheduled" : ""}</span><span className="text-xs text-muted-foreground">{fmtTime(l.called_at)}</span><span className="ml-auto"><ResultBadge r={l.result} /></span></div>
            {l.error && <p className="mt-1 rounded-lg bg-red-50 px-2 py-1 text-xs text-red-700" data-testid="test-log-error">{l.error}</p>}
            <p className="mt-1 text-xs text-muted-foreground">{l.steps.map((s) => s.event).join(" → ")}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
